import {useMutation, useQueryClient} from '@tanstack/react-query';
import {isAxiosError} from 'axios';
import {
  bulkDeleteNamespaceNotifications,
  bulkEnableNamespaceNotifications,
  createNamespaceNotification,
  NamespaceNotification,
  testNamespaceNotification,
} from 'src/resources/NamespaceNotificationResource';

export function useUpdateNamespaceNotifications(
  orgname: string,
  isUser = false,
) {
  const queryClient = useQueryClient();

  const {
    mutate: create,
    error: errorCreatingNotification,
    isSuccess: successCreatingNotification,
    isLoading: isCreatingNotification,
    reset: resetCreatingNotification,
  } = useMutation(
    async (notification: NamespaceNotification) =>
      createNamespaceNotification(orgname, notification, isUser),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['namespacenotifications']);
      },
    },
  );

  const {
    mutate: removeNotification,
    isError: errorDeletingNotification,
    isSuccess: successDeletingNotification,
    reset: resetDeletingNotification,
  } = useMutation(
    async (uuids: string | string[]) => {
      uuids = Array.isArray(uuids) ? uuids : [uuids];
      return bulkDeleteNamespaceNotifications(orgname, uuids, isUser);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['namespacenotifications']);
      },
    },
  );

  const {
    mutate: test,
    isError: errorTestingNotification,
    isSuccess: successTestingNotification,
    reset: resetTestingNotification,
  } = useMutation(
    async (uuid: string) => testNamespaceNotification(orgname, uuid, isUser),
    {
      onSuccess: () => {
        setTimeout(() => {
          queryClient.invalidateQueries({queryKey: ['namespacenotifications']});
        }, 2000);
      },
    },
  );

  const {
    mutate: enable,
    isError: errorEnablingNotification,
    isSuccess: successEnablingNotification,
    reset: resetEnablingNotification,
  } = useMutation(
    async (uuids: string | string[]) => {
      uuids = Array.isArray(uuids) ? uuids : [uuids];
      return bulkEnableNamespaceNotifications(orgname, uuids, isUser);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['namespacenotifications']);
      },
    },
  );

  let errorCreationMessage: string | null = null;
  if (errorCreatingNotification != null) {
    errorCreationMessage =
      isAxiosError(errorCreatingNotification) &&
      errorCreatingNotification.response?.data?.detail
        ? errorCreatingNotification.response.data.detail
        : 'Unable to create notification';
  }

  return {
    create,
    successCreatingNotification,
    isCreatingNotification,
    errorCreatingNotification: errorCreationMessage,
    resetCreatingNotification,

    deleteNotifications: removeNotification,
    errorDeletingNotification,
    successDeletingNotification,
    resetDeletingNotification,

    test,
    errorTestingNotification,
    successTestingNotification,
    resetTestingNotification,

    enableNotifications: enable,
    errorEnablingNotification,
    successEnablingNotification,
    resetEnablingNotification,
  };
}
